<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Alert, Button, Col, Input, Row } from '@sveltestrap/sveltestrap';
  import { parse, unparse } from 'papaparse';

  import { Carousel } from '@client/components/carousel';
  import { CsvPreview } from '@client/components/csv-preview';
  import { Icon } from '@client/components/icon';
  import { SectionItem } from '@client/components/section';
  import { Select } from '@client/components/select';
  import { Spinner } from '@client/components/spinner';
  import { Step } from '@client/components/steps';

  import { GroupsService } from '@client/services/groups';
  import { SignaturesService } from '@client/services/signatures';
  import { TagBlocksService } from '@client/services/tag-blocks';

  import { updatePushHistory } from '@client/stores/push-history';
  import { pusherStore } from '@client/stores/pusher';
  import { userStore } from '@client/stores/user';

  import { PUSH_ACTION_MODE, PUSH_ACTION_TYPES } from '@client/enums';

  import { debounce } from '@common/utils/debounce';

  import { AppendHtml, Banner, FindReplace } from './sections/configuration';
  import { ExcludeGroups, Upload } from './sections/data-source';
  import { createSignaturePlaceholder, createSignatureTemplate, getBannerName } from '../utils';
  import { ConfigurationSection, PushSection } from './sections';
  import StatusModal from './status-modal';

  export { history as config };

  export let pushed = false;
  export let workflowMode = false;

  let activeStep = 'configuration';
  let currentIndex = 0;
  let expandedReview = false;
  let history = {};
  let loaded = false;
  let modeOptions = Object.keys(PUSH_ACTION_MODE).map((key) => ({ label: $format(PUSH_ACTION_MODE[key]), value: key }));
  let showStatus = false;
  let signatureOptions = [];
  let signatures = [];

  export let pushTo;

  let actionOptions = [
    {
      label: $format('label.UPDATE'),
      value: 'update'
    },
    {
      label: $format('label.DELETE'),
      value: 'delete'
    }
  ];

  let typeOptions = [
    {
      label: $format('label.PLAIN_TEXT'),
      value: 'plain'
    },
    {
      label: $format('label.VISUAL_PREVIEW'),
      value: 'visual'
    }
  ];

  let sourceOptions = [
    {
      label: $format('label.UPLOAD_SPREADSHEET'),
      value: 'upload'
    },
    {
      label: $format('label.MANUAL_ENTRY'),
      value: 'manual'
    }
  ];

  // TODO condense below to config function based on history and/or defaults

  const find = (options, historyValue) => {
    const match = options.find(({ value }) => value === historyValue);

    return match || options[0];
  };

  let config = {
    action: find(actionOptions, history.note?.pushAction),
    mode: find(modeOptions, history.note?.pushMode),
    prependLines: 0,
    pushMode: history.note?.pushMode || 'signature',

    // data source
    csv: history.note?.sigdata ? unparse(history.note?.sigdata) : null,
    csvConfig: history.note?.sigdata ? { sigdata: history.note?.sigdata } : {},
    exclusions: [],
    source: find(sourceOptions, 'manual'), // Always manual, even for re-push

    // find replace
    regexp: history.note?.regexp || '',
    change: history.note?.change || '',
    flags: history.note?.flags || '',

    // appendHTML
    appendHTML: !!history.note?.appendHTML,
    appendHTMLTemplate: history.note?.appendHTMLTemplate || '',
    doNotClearUnknown: history.note && 'doNotClearUnknown' in history.note ? history.note?.doNotClearUnknown : true,

    // signature
    pushVisual: history.note?.pushVisual || false,
    signature: null,
    type: find(typeOptions, history.note?.pushVisual ? 'visual' : 'plain'),

    // banners
    altText: history.note?.altText,
    appendImage: !!history.note?.appendImage,
    disableRTP: !!history.note?.disableRTP,
    imagePlaceholder: history.note?.imagePlaceholder || '',
    removeBanner: !!history.note?.removeBanner,

    // email reminder
    emailbody: history.note?.emailbody || '',
    emailsubject: history.note?.emailsubject || ''
  };

  const cache = {};

  const getUserSignature = async ({ rulename, originalParams = [], userParams, template }) => {
    let email = $userStore.currentUser.email;
    const isDefaultSignature = !rulename;

    if (!isDefaultSignature && cache[rulename] && !userParams) {
      return cache[rulename];
    }

    if (!userParams) {
      const response = await GroupsService.findGroupByEmail({ email });

      const [item] = response?.length ? response[0].senders[0].username : [];

      if (item?.trim() !== '*') {
        email = item;
      }
    }

    let data = {
      username: email
    };

    if (!isDefaultSignature) {
      data = {
        ...data,
        scratchblockname: rulename,
        testData: {
          testsender: email
        }
      };

      if (template) {
        const params = originalParams.map((item) => ({ ...item, default: userParams?.[item.name] || item.default }));

        if (userParams?.username) {
          const username = `${userParams.username}@${$userStore.domain}`;

          data = {
            ...data,
            username,
            scratchblockname: undefined,
            testData: {
              testsender: username
            }
          };
        }

        data = {
          ...data,
          scratchtemplate: template,
          scratchparams: params
        };
      }
    }

    const { rendered } = await SignaturesService.getSignature(data);

    if (!isDefaultSignature) {
      cache[rulename] = rendered;
    }

    return rendered;
  };

  const resizeIframe = function () {
    const divisor = expandedReview ? 1 : 1.75;
    this.style.visibility = 'visible';
    this.style.width = '100%';
    this.style.height = (this.contentWindow.document.body.firstChild.clientHeight + 8) / divisor + 'px';
  };

  $: showReviewSection = config.mode.value === 'signature' && config.action.value === 'update';
  $: canReview = sigdata.data?.length && !sigdata.errors.length;

  onMount(async () => {
    const response = await TagBlocksService.getAll();

    signatures = response.filter((sig) => !sig.archived).sort((a, b) => a.rulename.localeCompare(b.rulename));

    signatureOptions = signatures.map(({ rulename }) => ({ label: rulename, value: rulename }));

    if (signatureOptions.length) {
      config.signature = history.note?.rulename
        ? {
            label: history.note.rulename,
            value: history.note.rulename
          }
        : signatureOptions[0];
    }

    config = { ...config };

    loaded = true;
  });

  const updatePreview = debounce((value) => {
    currentIndex = 0;

    config.csv = value.replace(/[\r\n]+/g, '\n');

    config.csvConfig.sigdata = parse(value, {
      delimiter: ',',
      header: true,
      skipEmptyLines: true,
      comments: '#'
    });

    config = { ...config };
  }, 800);

  const saveSignatureConfig = () => {
    const signature = signatures.find(({ rulename }) => rulename === config.signature.value);

    if (signature) {
      config.signatureConfig = {
        rulename: signature.rulename,
        sigTemplate: createSignatureTemplate(signature),
        template: signature.htmltemplate,
        originalParams: signature.params,
        sigcode: signature.sigcode,
        params: signature.params.reduce((acc, item) => {
          acc[item.name] = item;
          return acc;
        }, {})
      };

      config = { ...config };
    }
  };

  const onPageChange = ({ detail }) => {
    currentIndex = detail;
  };

  const onPush = async (service) => {
    let result;

    pushTo = service;

    // Only show status modal if not in workflow mode
    if (!workflowMode) {
      showStatus = true;
    }

    switch (service) {
      case 'front':
        result = await pusherStore.pushToFront(config);
        break;
      case 'google':
        result = await pusherStore.pushToGoogle(config);
        break;
      case 'o365':
        result = await pusherStore.pushToO365(config);
        break;
      case 'outreach':
        result = await pusherStore.pushToOutreach(config);
        break;
      case 'salesforce':
        result = await pusherStore.pushToSalesforce(config);
        break;
      case 'email':
        result = await pusherStore.pushToEmail(config);
        break;
    }

    pushed = true;
    updatePushHistory();
  };

  $: sigTemplate = createSignatureTemplate(signatures.find(({ rulename }) => rulename === config.signature.value));
  $: config.signature && saveSignatureConfig();
  $: config.csvConfig && pusherStore.reset();
  $: sigdata = config.csvConfig?.sigdata || { data: [], errors: [] };

  $: if (config.mode) {
    config.pushMode = config.mode.value;
    config.appendHTML = config.mode.value === 'appendhtml';
    config.appendOnly = config.mode.value !== 'signature';
  }

  $: legacyPush = history.note && !history.note.pushAction;

  // Signature dynamic values
  $: if (config.type) {
    config.pushVisual = config.type.value === 'visual';
  }

  // Banner dynamic values
  $: if (config.action && config.mode?.value === 'banner') {
    config.altText = 'banner';
    config.removeBanner = config.action.value === 'delete';
  }

  $: csvPlaceholder = createSignaturePlaceholder(config.pushMode, config.action.value, config.signatureConfig);

  // Steps dynamic values
  $: dataSourceValid = sigdata.data?.length && !sigdata.errors?.length;
  $: modeConfigValid =
    config.mode.value === 'banner' ||
    config.mode.value === 'signature' ||
    (config.mode.value === 'appendhtml' && config.appendHTMLTemplate?.trim().length) ||
    (config.mode.value === 'findandreplace' && config.regexp?.length);
</script>

<div>
  {#if legacyPush}
    <Alert color="info" dismissible>
      {$format('label.PUSHER_LEGACY_PUSH')}
    </Alert>
  {/if}
  <Step
    active={activeStep === 'configuration'}
    summary={$format('label.PUSHER_CONFIGURATION_SUMMARY', {
      values: { mode: config.mode?.value, action: config.action?.value }
    })}
    title={$format('label.CONFIGURATION')}
    onNext={() => {
      if (config.action.value === 'delete') {
        activeStep = 'datasource';
      } else {
        activeStep = config.mode.value;
      }
    }}
    onStep={() => (activeStep = 'configuration')}
  >
    <ConfigurationSection bind:action={config.action} bind:mode={config.mode} />
  </Step>

  {#if config.mode.value === 'signature' && config.action.value === 'update'}
    <Step
      active={activeStep === 'signature'}
      summary={config.signatureConfig?.rulename}
      title={$format('label.SIGNATURE')}
      onBack={() => (activeStep = 'configuration')}
      onNext={() => (activeStep = 'datasource')}
      onStep={() => (activeStep = 'signature')}
    >
      <SectionItem
        label={$format('label.SIGNATURE_TYPE')}
        description={$format('label.DESCRIPTION.SIGNATURE_TYPE')}
        tooltip={$format('label.TOOLTIP.SIGNATURE_TYPE')}
      >
        <Select
          bind:items={typeOptions}
          bind:value={config.type}
          placeholder={$format('label.PLACEHOLDER.SELECT_SIGNATURE_TYPE')}
          clearable={false}
          showChevron
        />
      </SectionItem>
      <SectionItem
        label={$format('label.SIGNATURE_TEMPLATE')}
        description={$format('label.DESCRIPTION.SIGNATURE_TEMPLATE')}
        tooltip={$format('label.TOOLTIP.SIGNATURE_TEMPLATE')}
        borderless
        disabled={!loaded}
      >
        <Select
          bind:items={signatureOptions}
          bind:value={config.signature}
          placeholder={$format('label.PLACEHOLDER.SELECT_SIGNATURE')}
          clearable={false}
          showChevron
        />
      </SectionItem>
      {#if config.signature}
        <Row>
          <Col>
            <b>{$format('label.PLAIN_TEXT')}</b>
            <pre class="source plain">{sigTemplate}</pre>
          </Col>
          {#if config.pushVisual}
            <Col sm={12} md={6}>
              <b>{$format('label.VISUAL_PREVIEW')}</b>

              <div class="source">
                {#await getUserSignature({ rulename: config.signature.value })}
                  <div class="loading">
                    <Spinner show inline />
                  </div>
                {:then html}
                  <iframe
                    tabindex="-1"
                    style="width: 100%; visibility: hidden"
                    scrolling="no"
                    on:load={resizeIframe}
                    title={config.signature.value}
                    srcdoc="<html><body><div style='transform: scale(.5,.5); transform-origin: left top; -moz-transform:  scale(.5,.5); -moz-transform-origin: left top;'>{html}</div></body></html>"
                  />
                {:catch error}
                  <p class="text-danger">{error.message}</p>
                {/await}
              </div>
            </Col>
          {/if}
        </Row>
      {/if}
    </Step>
  {/if}

  {#if config.mode.value === 'appendhtml'}
    <Step
      active={activeStep === 'appendhtml'}
      nextDisabled={!config.appendHTMLTemplate?.trim().length}
      summary={$format('label.PUSHER_APPENDHTML_SUMMARY', {
        values: { lines: config.appendHTMLTemplate.trim().length ? config.appendHTMLTemplate?.split('\n').length : 0 }
      })}
      title={$format('label.APPEND_HTML')}
      onBack={() => (activeStep = 'configuration')}
      onNext={() => (activeStep = 'datasource')}
      onStep={() => (activeStep = 'appendhtml')}
    >
      <AppendHtml bind:preserveUnknown={config.doNotClearUnknown} bind:html={config.appendHTMLTemplate} />
    </Step>
  {/if}

  {#if config.mode.value === 'banner' && config.action.value === 'update'}
    <Step
      active={activeStep === 'banner'}
      summary={config.appendImage && getBannerName(config.imagePlaceholder)}
      title={$format('label.BANNER')}
      onBack={() => (activeStep = 'configuration')}
      onNext={() => (activeStep = 'datasource')}
      onStep={() => (activeStep = 'banner')}
    >
      <Banner
        bind:altText={config.altText}
        bind:appendImage={config.appendImage}
        bind:imagePlaceholder={config.imagePlaceholder}
      />
    </Step>
  {/if}

  {#if config.mode.value === 'findandreplace'}
    <Step
      active={activeStep === 'findandreplace'}
      nextDisabled={!config.regexp?.length}
      summary={config.regexp + ' → ' + config.change}
      title={$format('label.FIND_AND_REPLACE')}
      onBack={() => (activeStep = 'configuration')}
      onNext={() => (activeStep = 'datasource')}
      onStep={() => (activeStep = 'findandreplace')}
    >
      <FindReplace bind:change={config.change} bind:flags={config.flags} bind:regexp={config.regexp} />
    </Step>
  {/if}

  <Step
    active={activeStep === 'datasource'}
    disabled={!modeConfigValid}
    nextDisabled={!(modeConfigValid && dataSourceValid)}
    summary={sigdata.data?.length &&
      $format('label.PUSHER_DATASOURCE_SUMMARY', {
        values: { users: sigdata.data?.length, groups: config.exclusions?.length }
      })}
    title={$format('label.DATA_SOURCE')}
    onBack={() => (activeStep = config.mode.value)}
    onNext={() => {
      if (showReviewSection) {
        activeStep = 'review';
      } else {
        activeStep = 'push';
      }
    }}
    onStep={() => (activeStep = 'datasource')}
  >
    <SectionItem
      label={$format('label.SOURCE')}
      description={$format('label.DESCRIPTION.SOURCE')}
      tooltip={$format('label.TOOLTIP.SOURCE')}
    >
      <Select
        bind:items={sourceOptions}
        bind:value={config.source}
        placeholder={$format('label.PLACEHOLDER.SELECT_DATA_SOURCE')}
        clearable={false}
        showChevron
      />
    </SectionItem>

    <ExcludeGroups bind:excludedGroups={config.exclusions} defaultGroups={history.note?.exclusions} />

    {#if config.source.value === 'upload'}
      <Upload onReset={() => updatePreview('')} onUpload={updatePreview} />
    {/if}

    {#if config.source.value === 'manual' || config.csv}
      <SectionItem label={$format('label.CSV')} fill borderless>
        <Input
          class="font-monospace"
          feedback={config.csv && !sigdata.meta?.fields.includes('username')
            ? $format('label.PUSHER_CSV_USERNAME')
            : undefined}
          invalid={sigdata.errors.length || (config.csv && !sigdata.meta?.fields.includes('username'))}
          placeholder={csvPlaceholder}
          rows={3}
          type="textarea"
          value={config.csv}
          on:keyup={(e) => updatePreview(e.target.value)}
        />
        {#if sigdata.data.length}
          <CsvPreview errors={sigdata.errors} headers={sigdata.meta?.fields} rows={sigdata.data} />
        {/if}
      </SectionItem>
    {/if}
  </Step>

  {#if showReviewSection}
    <Step
      active={activeStep === 'review'}
      disabled={!(modeConfigValid && dataSourceValid)}
      nextDisabled={!(modeConfigValid && dataSourceValid)}
      title={$format('label.REVIEW')}
      onBack={() => (activeStep = 'datasource')}
      onNext={() => (activeStep = 'push')}
      onStep={() => (activeStep = 'review')}
    >
      <SectionItem fill borderless>
        {#key sigdata}
          {#if sigdata.data.length}
            <div class="review-header">
              <div>
                {$format('label.REVIEWING_USERS', {
                  values: { n: currentIndex + 1, total: sigdata.data.length }
                })}
              </div>
              <div class="username">{sigdata.data[currentIndex].username}</div>
              <Button
                class="toggle-size"
                color="link"
                aria-label={expandedReview ? $format('label.COLLAPSE') : $format('label.EXPAND')}
                title={expandedReview ? $format('label.COLLAPSE') : $format('label.EXPAND')}
                on:click={() => (expandedReview = !expandedReview)}
              >
                <Icon name={expandedReview ? 'minimize-2' : 'maximize-2'} />
              </Button>
            </div>
          {/if}
        {/key}
      </SectionItem>

      {#if canReview && config.mode.value === 'signature' && config.action.value === 'update' && config.signatureConfig}
        <div class="review" class:single={sigdata.data.length === 1} class:expanded={expandedReview}>
          {#key sigdata.data}
            <Carousel
              items={sigdata.data}
              ride={false}
              showControls={sigdata.data.length > 1}
              bind:activeIndex={currentIndex}
              let:index
              let:item
            >
              <div class="horizontal-layout">
                {#if !(config.pushVisual || config.mode.value === 'appendhtml')}
                  <div>
                    <div class="source plain">
                      [[{config.signatureConfig.sigcode}]]
                      <br />
                      {#each Object.keys(config.signatureConfig.params) as key}
                        {#if !config.signatureConfig.params[key].hide}
                          {#if item[key]}
                            &nbsp;&nbsp;{key}: {item[key]}
                            <br />
                          {:else}
                            &nbsp;&nbsp;{key}:
                            <br />
                          {/if}
                        {/if}
                      {/each}
                      [[{config.signatureConfig.sigcode}]]
                    </div>
                  </div>
                {:else}
                  <div>
                    <div class="source" class:visual-only={config.pushVisual}>
                      {#if currentIndex === index}
                        {#await getUserSignature( { ...config.signatureConfig, rulename: config.signatureConfig.rulename, userParams: item } )}
                          <div class="loading">
                            <Spinner show inline />
                          </div>
                        {:then html}
                          <iframe
                            tabindex="-1"
                            style="width: 100%; visibility: hidden"
                            scrolling="no"
                            on:load={resizeIframe}
                            title={config.signature.value}
                            srcdoc={`<html><body><div style='transform: scale(${expandedReview ? 1 : 0.5},${expandedReview ? 1 : 0.5}); transform-origin: left top; -moz-transform: scale(${expandedReview ? 1 : 0.5},${expandedReview ? 1 : 0.5}); -moz-transform-origin: left top;'>${html}</div></body></html>`}
                          />
                        {:catch error}
                          <p class="text-danger">{error.message}</p>
                        {/await}
                      {/if}
                    </div>
                  </div>
                {/if}
              </div>
            </Carousel>
          {/key}
        </div>
      {/if}
    </Step>
  {/if}

  <Step
    active={activeStep === 'push'}
    disabled={!(modeConfigValid && dataSourceValid)}
    summary={$format(PUSH_ACTION_TYPES[history.action])}
    title={$format('label.PUSH')}
    onBack={() => {
      if (showReviewSection) {
        activeStep = 'review';
      } else {
        activeStep = 'datasource';
      }
    }}
    onStep={() => (activeStep = 'push')}
  >
    <PushSection
      deleting={config.action.value === 'delete'}
      previousPush={history.action}
      pushing={$pusherStore.pushing}
      signature={config.mode.value === 'signature'}
      visual={config.pushVisual}
      bind:emailsubject={config.emailsubject}
      bind:emailbody={config.emailbody}
      {onPush}
    />
  </Step>
</div>

{#if showStatus}
  <StatusModal
    bind:show={showStatus}
    failures={$pusherStore.failures}
    fields={config.csvConfig?.sigdata?.meta?.fields}
    pushing={$pusherStore.pushing}
    results={$pusherStore.pushResults}
    rows={config.csvConfig?.sigdata?.data}
    successes={$pusherStore.successes}
    total={config.csvConfig?.sigdata?.data?.length}
    type={pushTo}
  />
{/if}

<style lang="scss" src="./pusher.scss"></style>
